/**
 * A day-by-day bar chart, for anything countable.
 *
 * Deliberately separate from `stats/BarSeries`, which is typed to the artist
 * dashboard's `DayPoint` and speaks in plays. Making that one generic would
 * have meant a `label` prop, a `field` prop and a component that no longer
 * says what it draws. Two small charts beat one configurable one.
 *
 * ## The empty days are the point
 *
 * The server returns every day in the window including the ones with nothing in
 * them, and this draws them. A chart built only from days that had something
 * puts four bars evenly across a month and reads as steady activity, when what
 * happened was four events a fortnight apart.
 *
 * ## Scaled to its own peak
 *
 * Not to a fixed ceiling. Four signups should look like the shape of four
 * signups rather than a flat line at the bottom of a chart built for thousands.
 * The peak is labelled, so the height still means something.
 */
export interface Point {
  date: string;
  value: number;
}

export function Series({
  points,
  label,
  tone = 'accent',
  height = 90,
}: {
  points: Point[];
  /** Singular noun for the tooltip: "signup", "upload", "play". */
  label: string;
  tone?: 'accent' | 'fresh' | 'hot';
  height?: number;
}) {
  const peak = Math.max(1, ...points.map((p) => p.value));
  const total = points.reduce((sum, p) => sum + p.value, 0);

  const fill = { accent: 'bg-accent', fresh: 'bg-fresh', hot: 'bg-hot' }[tone];

  return (
    <div className="flex flex-col gap-2" data-series={label}>
      <div className="flex items-end gap-[2px]" style={{ height }}>
        {points.map((point) => (
          <span
            key={point.date}
            title={`${point.value.toLocaleString()} ${point.value === 1 ? label : `${label}s`} on ${day(point.date)}`}
            className={`min-w-px flex-1 rounded-[2px] ${point.value > 0 ? fill : 'bg-track-dim'}`}
            /* A floor of 2px, so a day with nothing is still a day rather than
               a gap that reads as missing data. */
            style={{ height: `${Math.max(2, (point.value / peak) * 100)}%` }}
          />
        ))}
      </div>

      <div className="flex justify-between font-mono text-[10px] text-ghost">
        <span>{points.length > 0 ? day(points[0]!.date) : ''}</span>
        <span>
          {total.toLocaleString()} total · peak {peak.toLocaleString()}
        </span>
        <span>today</span>
      </div>
    </div>
  );
}

function day(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);

  return Number.isNaN(date.valueOf())
    ? iso
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
